import { ChevronLeft, ChevronRight } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { useRef, useState } from "preact/hooks";

const ARROW_CLASS =
  "hidden md:flex absolute top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/95 dark:bg-neutral-800/95 shadow-md items-center justify-center hover:bg-white dark:hover:bg-neutral-700 transition-colors";

const GAP = 16;

type Edges = { start: boolean; end: boolean; current: number };

const Dots = ({ count, current, onSelect }: { count: number; current: number; onSelect: (index: number) => void }) => (
  <div class="mt-1 flex items-center justify-center gap-1.5">
    {Array.from({ length: count }, (_, index) => (
      <button
        key={index}
        type="button"
        onClick={() => onSelect(index)}
        aria-label={`Go to ${index + 1}`}
        class={`rounded-full transition-all duration-200 ${index === current ? "w-2 h-2 bg-gray-800 dark:bg-gray-300" : "w-1.5 h-1.5 bg-gray-400 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-400"}`}
      />
    ))}
  </div>
);

export const Strip = ({ children }: { children: ComponentChildren[] }) => {
  const list = useRef<HTMLUListElement | null>(null);
  const [edges, setEdges] = useState<Edges>({ start: true, end: false, current: 0 });

  const measure = () => {
    const element = list.current;
    if (!element) {
      return;
    }
    const end = element.scrollLeft + element.clientWidth >= element.scrollWidth - 4;
    const slide = (element.firstElementChild?.clientWidth ?? element.clientWidth) + GAP;
    const current = end ? children.length - 1 : Math.round(element.scrollLeft / slide);
    setEdges({ start: element.scrollLeft <= 4, end, current });
  };

  const scrollToIndex = (index: number) => {
    const element = list.current;
    const slide = (element?.firstElementChild?.clientWidth ?? 0) + GAP;
    element?.scrollTo({ left: index * slide, behavior: "smooth" });
  };

  const scrollBy = (direction: number) => {
    const element = list.current;
    element?.scrollBy({ left: direction * element.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <div class="relative">
      {!edges.start && (
        <button type="button" class={`${ARROW_CLASS} left-2`} onClick={() => scrollBy(-1)} aria-label="Previous">
          <ChevronLeft size={20} class="text-gray-700 dark:text-gray-300" />
        </button>
      )}
      {!edges.end && (
        <button type="button" class={`${ARROW_CLASS} right-2`} onClick={() => scrollBy(1)} aria-label="Next">
          <ChevronRight size={20} class="text-gray-700 dark:text-gray-300" />
        </button>
      )}
      <ul ref={list} onScroll={measure} class="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
        {children.map((child, index) => (
          <li key={index} class="w-full md:w-96 shrink-0 snap-start">
            {child}
          </li>
        ))}
      </ul>
      <Dots count={children.length} current={edges.current} onSelect={scrollToIndex} />
    </div>
  );
};
